import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';

const ADDED_STATE_DURATION = 2000;
const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
/* Header menus (drawer, mega menu, localization) stack with plain z-index —
 * --layer-menu-drawer (18) and below — while the bar is a top-layer popover,
 * which paints above ANY z-index. The full-screen mobile drawer covers the
 * viewport, so the bar has to stand down for every placement, not just top. */
const HEADER_MENU_OPEN_SELECTOR = [
  // Exclude accordions the way header-drawer.js' own close does: it resets
  // `details[open]:not(accordion-custom > details)`, deliberately LEAVING drawer
  // accordions open, and drawer_accordion_expand_first ships one open at load.
  'header-drawer details[open]:not(accordion-custom > details)',
  'header-menu details[open]:not(accordion-custom > details)',
  '.menu-drawer-container[open]',
  'header [aria-expanded="true"]',
  '.header [aria-expanded="true"]',
  '.shopify-section-group-header-group [aria-expanded="true"]',
].join(', ');

/**
 * Only header UI that is actually ON SCREEN should stand the bar down. A closed
 * drawer keeps its markup at `visibility: hidden`, so an accordion left open
 * inside it still matches the selector — without this gate the bar would be
 * suppressed permanently, from first load.
 *
 * No checkVisibility (pre-Chrome 105 / Safari 17.4) means no popover either, so
 * the bar isn't in the top layer and has no header UI to cover: never hide.
 *
 * @param {Element} element
 * @returns {boolean}
 */
const isVisibleHeaderMenu = (element) =>
  typeof element.checkVisibility === 'function' &&
  element.checkVisibility({ visibilityProperty: true, contentVisibilityAuto: true });
const DEFAULT_SCROLL_DISTANCE = 100;

/**
 * Standalone buy button for the product-buy-button block.
 *
 * Add-to-cart mode mirrors cart-upsell.js: intercepts the block's /cart/add
 * form submit, stamps attribution line item properties, posts with the cart
 * section IDs so the drawer HTML comes back in the response, dispatches
 * CartAddEvent (absolute item count — see cart-icon.js source handling),
 * then opens the cart drawer. Without JS the form posts natively.
 *
 * Express-checkout mode enhances the cart-permalink anchor so marketing
 * params survive — attribution.js only decorates /checkout links, not
 * /cart/VARIANT:QTY permalinks.
 *
 * Sticky mode (data-sticky) floats the SAME cta — the block root stays in flow
 * at zero height and doubles as the scroll sentinel, so both actions keep their
 * markup, refs and declarative event bindings (which walk DOM ancestors; the
 * bar must never be portaled out of the component).
 *
 * @extends {Component}
 */
class ProductBuyButtonComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController | null} */
  #stickyController = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {number} */
  #stickyFrame = 0;

  /** @type {boolean} */
  #footerVisible = false;

  /** @type {boolean} */
  #stickyForced = false;

  /** @type {boolean} */
  #headerMenuOpen = false;

  /** @type {MutationObserver | null} */
  #headerObserver = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.sticky === 'true') this.#initSticky();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickyController?.abort();
    this.#stickyController = null;
    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
    this.#headerObserver?.disconnect();
    this.#headerObserver = null;
    if (this.#stickyFrame) cancelAnimationFrame(this.#stickyFrame);
    this.#stickyFrame = 0;
  }

  /**
   * Wires up the reveal triggers for sticky mode.
   */
  #initSticky() {
    if (!(this.refs.stickyBar instanceof HTMLElement)) return;

    this.#stickyController = new AbortController();
    const { signal } = this.#stickyController;

    window.addEventListener('scroll', this.#requestStickyUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#requestStickyUpdate, { passive: true, signal });

    if (this.dataset.stickyHideAtFooter === 'true') this.#observeFooter();
    this.#observeHeaderMenus();

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', this.#updateViewportInset, { passive: true, signal });
      viewport.addEventListener('scroll', this.#updateViewportInset, { passive: true, signal });
      this.#updateViewportInset();
    }

    // The editor scrolls to the block's (zero-height) flow position on select,
    // which on its own reveals nothing — pin the bar open while it is selected.
    if (/** @type {any} */ (window).Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, { signal });
    }

    this.#updateSticky();
  }

  /**
   * Hides the bar once the footer is in view, mirroring sticky-add-to-cart.js.
   */
  #observeFooter() {
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        this.#footerVisible = entry.isIntersecting;
        this.#updateSticky();
      },
      { rootMargin: '200px 0px 0px 0px' }
    );
    this.#footerObserver.observe(footer);
  }

  /**
   * Stands the bar down while header UI is expanded and VISIBLE. The drawer is
   * matched by `details[open]`; `aria-expanded` covers the header-menu buttons,
   * which are the only header controls that carry it.
   *
   * Scope the SELECTOR to header components, not the observed root: Shopify
   * stamps `shopify-section-group-header-group` on EVERY section in the group,
   * so `querySelector` on that class can land on the announcement bar and never
   * see the drawer. Watching document.body for just these two attribute names
   * is cheap — they barely mutate outside menus.
   */
  #observeHeaderMenus() {
    const sync = () => {
      const open = Array.from(document.querySelectorAll(HEADER_MENU_OPEN_SELECTOR)).some(isVisibleHeaderMenu);
      if (open === this.#headerMenuOpen) return;
      this.#headerMenuOpen = open;
      this.#updateSticky();
    };

    this.#headerObserver = new MutationObserver(sync);
    this.#headerObserver.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-expanded'],
    });
    sync();
  }

  #requestStickyUpdate = () => {
    if (this.#stickyFrame) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = 0;
      this.#updateViewportInset();
      this.#updateSticky();
    });
  };

  /**
   * Lifts the bar by however much of the layout viewport the visual viewport
   * doesn't cover — mobile browser chrome, or the on-screen keyboard. Reading
   * visualViewport doesn't force layout, so this is safe per frame.
   */
  #updateViewportInset = () => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const inset = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
    this.style.setProperty('--sticky-viewport-inset', `${inset}px`);
  };

  #updateSticky() {
    const { stickyBar } = this.refs;
    if (!(stickyBar instanceof HTMLElement)) return;

    const stuck = this.#stickyForced || this.#shouldStick();
    if (stickyBar.dataset.stuck === String(stuck)) return;
    stickyBar.dataset.stuck = String(stuck);

    // Top-layer promotion (see the popover note in the block's stylesheet).
    // Older browsers without the popover API keep the plain fixed bar.
    if (typeof stickyBar.showPopover !== 'function') return;
    try {
      const open = stickyBar.matches(':popover-open');
      if (stuck && !open) stickyBar.showPopover();
      else if (!stuck && open) stickyBar.hidePopover();
    } catch {
      // Popover state raced another call — the data-stuck styling still applies.
    }
  }

  /**
   * @returns {boolean} Whether the floating bar should be showing right now.
   */
  #shouldStick() {
    if (this.dataset.stickyDevices !== 'all' && window.matchMedia(DESKTOP_MEDIA_QUERY).matches) return false;
    if (this.#footerVisible || this.#headerMenuOpen) return false;

    switch (this.dataset.stickyTrigger) {
      case 'immediate':
        return true;
      case 'scroll': {
        const distance = Number(this.dataset.stickyScrollDistance) || DEFAULT_SCROLL_DISTANCE;
        return window.scrollY >= (window.innerHeight * distance) / 100;
      }
      default:
        // The root sits at the block's place in the page — zero-height when the
        // button only floats, the inline button itself when it also renders in
        // place, so the bottom edge is the trigger point either way.
        return this.getBoundingClientRect().bottom <= 0;
    }
  }

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (target !== this && !(target instanceof Node && this.contains(target))) return;
    this.#stickyForced = true;
    this.#updateSticky();
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    const target = event.target;
    if (target !== this && !(target instanceof Node && this.contains(target))) return;
    this.#stickyForced = false;
    this.#updateSticky();
  };

  /**
   * @param {SubmitEvent} event
   */
  handleAddToCart = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const formData = new FormData(form);
    const variantId = String(formData.get('id') ?? button.dataset.variantId ?? '');
    if (!variantId) return;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const labelEl = button.querySelector('.product-buy-button__label') ?? button;
    const originalLabel = labelEl.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (button.dataset.addingLabel) labelEl.textContent = button.dataset.addingLabel;

    const attribution = /** @type {any} */ (window).Attribution;
    if (attribution && typeof attribution.buildLineItemProperties === 'function') {
      const properties = attribution.buildLineItemProperties(form, variantId) || {};
      for (const [key, value] of Object.entries(properties)) {
        formData.set(`properties[${key}]`, String(value));
      }
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
      .filter(Boolean);
    formData.append('sections', sectionIds.join(','));

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
        signal: abortController.signal,
      });
      const data = await response.json();

      if (data.status) {
        console.error(data.description || data.message);
        this.dispatchEvent(
          new CartErrorEvent(this.id || 'product-buy-button', data.message, data.description, data.errors)
        );
        labelEl.textContent = originalLabel;
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal: abortController.signal });
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'product-buy-button', {
          source: 'product-buy-button',
          itemCount: cart.item_count,
          variantId,
          sections: data.sections,
        })
      );

      if (attribution && typeof attribution.syncCartAttributes === 'function') {
        attribution.syncCartAttributes().catch(() => {});
      }

      this.#scheduleOfferReconcile(cart);

      const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else if (window.location.pathname !== Theme.routes.cart_url) {
        window.location.assign(Theme.routes.cart_url);
        return;
      }

      if (button.isConnected) {
        if (button.dataset.addedLabel) labelEl.textContent = button.dataset.addedLabel;
        setTimeout(() => {
          if (button.isConnected) {
            labelEl.textContent = originalLabel;
            button.disabled = false;
          }
        }, ADDED_STATE_DURATION);
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error(error);
      }
      labelEl.textContent = originalLabel;
    } finally {
      this.#activeFetch = null;
      button.removeAttribute('aria-busy');
      if (!labelEl.textContent || labelEl.textContent === button.dataset.addingLabel) {
        labelEl.textContent = originalLabel;
      }
      if (labelEl.textContent === originalLabel) {
        button.disabled = false;
      }
    }
  };

  /**
   * IOM applies funnel offer stamps and discounts via its own
   * /cart/change.js calls shortly AFTER the add, so the drawer HTML in the
   * add response predates the discount and nothing tells the theme to
   * re-render (the price stays full until the next cart mutation). Poll
   * /cart.js briefly and dispatch a cart update when the totals move so
   * cart sections repaint with the discounted price.
   *
   * `cartAtAdd` is a post-add /cart.js snapshot that RACES IOM's change.js:
   * if the discount landed before it, the baseline is already post-IOM and
   * totals never "move" — while the drawer still shows the pre-IOM add
   * response HTML. The first poll therefore always dispatches (a repaint of
   * server truth is a visual no-op when nothing changed), and the baseline
   * rolls forward so a later IOM pass still triggers the moved-totals path.
   * @param {{ total_price: number, total_discount: number }} cartAtAdd
   */
  #scheduleOfferReconcile(cartAtAdd) {
    let baseline = cartAtAdd;
    let attempts = 0;
    const check = async () => {
      attempts += 1;
      try {
        const fresh = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
        const moved = fresh.total_price !== baseline.total_price || fresh.total_discount !== baseline.total_discount;
        if (moved || attempts === 1) {
          this.dispatchEvent(
            new CartUpdateEvent(fresh, this.id || 'product-buy-button', {
              source: 'product-buy-button',
              itemCount: fresh.item_count,
            })
          );
          if (moved) return;
          baseline = fresh;
        }
      } catch {
        // Transient fetch failure — retry on the next attempt.
      }
      if (attempts < 3) setTimeout(check, attempts * 2000);
    };
    setTimeout(check, 1500);
  }

  /**
   * @param {MouseEvent} event
   */
  handleExpressCheckout = (event) => {
    const target = event.target;
    const anchor = target instanceof Element ? target.closest('a[href]') : null;
    if (!(anchor instanceof HTMLAnchorElement)) return;

    const attribution = /** @type {any} */ (window).Attribution;
    if (attribution && typeof attribution.appendMarketingParamsToUrl === 'function') {
      event.preventDefault();
      window.location.assign(attribution.appendMarketingParamsToUrl(anchor.href));
    }
    // Without Attribution the anchor navigates natively to the permalink.
  };
}

if (!customElements.get('product-buy-button-component')) {
  customElements.define('product-buy-button-component', ProductBuyButtonComponent);
}
